import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, ChevronRight } from "lucide-react";

export default function PlatformCompanies() {
  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", plan: "trial" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/platform/companies").then((r) => setCompanies(r.data.companies)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/platform/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 40);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/platform/companies", form);
      toast.success("Company created");
      setShowNew(false);
      setForm({ name: "", slug: "", plan: "trial" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="platform-companies-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-400" />
          <h1 className="text-2xl font-bold tracking-tight">Companies</h1>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          data-testid="new-company-button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-3.5 py-2 transition-colors"
        >
          <Plus className="w-4 h-4" /> New company
        </button>
      </div>

      {showNew && (
        <div className="rounded-xl bg-slate-900/70 ring-1 ring-white/5 p-5 mb-6 space-y-4" data-testid="new-company-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))}
                data-testid="new-company-name"
                className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                placeholder="Acme Ltd"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Slug</label>
              <input
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                data-testid="new-company-slug"
                className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                placeholder="acme"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Plan</label>
              <select
                value={form.plan}
                onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
                data-testid="new-company-plan"
                className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                {(meta?.plans || ["trial", "standard", "enterprise"]).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Modules default to the plan bundle{meta ? `: ${(meta.plan_modules[form.plan] || []).join(", ")}` : ""}. You can override them after creation.
          </p>
          <div className="flex gap-2">
            <button
              onClick={create}
              disabled={saving || !form.name || !form.slug}
              data-testid="create-company-submit"
              className="rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 font-semibold text-sm px-4 py-2 transition-colors"
            >
              {saving ? "Creating…" : "Create company"}
            </button>
            <button onClick={() => setShowNew(false)} className="rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {companies.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/platform/companies/${c.id}`)}
            data-testid={`company-card-${c.slug}`}
            className="text-left rounded-xl bg-slate-900/70 ring-1 ring-white/5 hover:ring-emerald-500/30 p-5 transition-all group"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-base">{c.name}</div>
                <div className="text-xs text-slate-500">/{c.slug}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400 transition-colors" />
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.status}</span>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/5 text-slate-300 capitalize">{c.plan}</span>
              <span className="text-xs text-slate-500">{c.member_count} members · {c.enabled_modules.length} modules</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
